import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  CircularProgress,
  FormControlLabel,
 Checkbox,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import useMediaQuery from "@mui/material/useMediaQuery";
import Header from "../../../../components/Header";
import {
  fetchDataEngine,
  generateAndExecuteMutation,
  graphqlQuery,
} from "../../../../data/Axios/DynamicService";
import { AccountsandFinanceurl, companyManagementUrl } from "../../../../config";
import { AllMpesaProviders, allRegions } from "../../../../data/Axios/queries";

const base_url = AccountsandFinanceurl.uri;
const base_url_company = companyManagementUrl.uri;

const MpesaAccountForm = (props) => {
  const { isEditing = false, data = {}, onClose, onAction } = props;

  const decodedToken = JSON.parse(localStorage.getItem("decodedToken") || "{}");
  const userId = parseInt(decodedToken.Id);
  const companyId = parseInt(decodedToken.CompanyId);

  const [providers, setProviders] = useState([]);
  const [regions, setRegions] = useState([]);
  const [branches, setBranches] = useState([]); // For selected region
  const [loading, setLoading] = useState(true);

  const isNonMobile = useMediaQuery("(min-width:600px)");

  // Fetch initial data: providers and regions
  useEffect(() => {
    async function fetchInitialData() {
      try {
        const [provRes, regRes] = await Promise.all([
          graphqlQuery(AllMpesaProviders, base_url),
          graphqlQuery(allRegions, base_url_company),
        ]);

        setProviders(provRes || []);
        setRegions(regRes || []);
      } catch (err) {
        console.error(err);
        Swal.fire("Error", "Failed to load form data", "error");
      } finally {
        setLoading(false);
      }
    }
    fetchInitialData();
  }, []);

  // Fetch branches when region changes
  const handleRegionChange = async (regionId, setFieldValue) => {
    if (!regionId) {
      setBranches([]);
      setFieldValue("branchId", "");
      return;
    }

    try {
      const inputFields = { regionId: parseInt(regionId) };
      const returnFields = ["id", "branchName"];
      const response = await fetchDataEngine(
        "allBranchesByColumnValues",
        "branchModel",
        inputFields,
        returnFields,
        base_url_company
      );

      const branchOptions = response
        ? response.map((b) => ({
            value: b.id,
            label: b.branchName,
          }))
        : [];

      setBranches(branchOptions);

      // Clear branch if current one doesn't belong to new region
      if (data?.branchId) {
        const valid = branchOptions.find((b) => b.value === data.branchId);
        if (!valid) {
          setFieldValue("branchId", "");
        }
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
      setBranches([]);
      Swal.fire("Error", "Failed to load branches", "error");
    }
  };

  // On mount: if editing and region is set, load branches
  useEffect(() => {
    if (isEditing && data?.regionId) {
      handleRegionChange(data.regionId, () => {}); // dummy setFieldValue
    }
  }, [isEditing, data?.regionId]);

  const initialValues = {
    providerId: data?.providerId || "",
    regionId: data?.regionId || "",
    branchId: data?.branchId || "",
    accountName: data?.accountName || "",
    tillPaybillNumber: data?.tillPaybillNumber || "",
    accountDescription: data?.accountDescription || "",
    status: data?.status ?? 1,
    isSaleAccount: data?.isSaleAccount || false,
  };

  const validationSchema = yup.object().shape({
    providerId: yup.number().required("Provider is required"),
    regionId: yup.number().required("Region is required"),
    branchId: yup.number().required("Branch is required"),
    accountName: yup.string().required("Account Name is required"),
  //  tillPaybillNumber: yup.string().required("Till/Paybill Number is required"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
   if (values.isSaleAccount) {
     // Check if another sale account exists in same branch
     const response = await fetchDataEngine(
       "anyMpesaAccountsByColumnValues",
       "mpesaAccounts",
       { branchId: parseInt(values.branchId), isSaleAccount: true },
       ["id", "accountName"],
      base_url
     );

     // If creating new or updating to true, only block if another exists
     if (
       response &&
       response.length > 0 &&
       (!isEditing || response[0].id !== data.id)
     ) {
       Swal.fire(
         "Warning",
         `There is already a Sale Account: <b>${response[0].accountName}</b>. Please uncheck it first.`,
         "warning"
       );
       setSubmitting(false);
       return;
     }
   }
      const mutationData = isEditing
        ? {
            id: data.id,
            providerId: parseInt(values.providerId),
            regionId: parseInt(values.regionId),
            branchId: parseInt(values.branchId),
            accountName: values.accountName,
            tillPaybillNumber: values.tillPaybillNumber,
            accountDescription: values.accountDescription,
            status: values.status,
            isSaleAccount: values.isSaleAccount,
            updatedBy: userId,
          }
        : {
            providerId: parseInt(values.providerId),
            regionId: parseInt(values.regionId),
            branchId: parseInt(values.branchId),
            accountName: values.accountName,
            tillPaybillNumber: values.tillPaybillNumber,
            accountDescription: values.accountDescription,
            status: values.status,
            isSaleAccount: values.isSaleAccount,
            createdBy: userId,
            companyId,
          };

      await generateAndExecuteMutation(
        isEditing ? "updateMpesaAccount" : "createMpesaAccount",
        mutationData,
        isEditing ? "updatedMpesaAccount" : "mpesaAccounts",
        base_url
      );

      Swal.fire("Success", `M-Pesa account ${isEditing ? "updated" : "created"} successfully`, "success");
      onAction();
      onClose();
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Failed to save M-Pesa account", "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <CircularProgress />;

  return (
    <Box m="20px">
      <Header
        title={isEditing ? "Edit M-Pesa Account" : "Create M-Pesa Account"}
        subtitle="Manage M-Pesa account details"
      />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          isSubmitting,
        }) => (
          <Form>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              sx={{
                "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
              }}
            >
              {/* Provider */}
              <FormControl fullWidth sx={{ gridColumn: "span 2" }}>
                <InputLabel>M-Pesa Provider *</InputLabel>
                <Select
                  name="providerId"
                  value={values.providerId}
                  onChange={handleChange}
                  error={touched.providerId && !!errors.providerId}
                >
                  {providers.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.providerName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Region */}
              <FormControl fullWidth sx={{ gridColumn: "span 2" }}>
                <InputLabel>Region *</InputLabel>
                <Select
                  name="regionId"
                  value={values.regionId}
                  onChange={(e) => {
                    handleChange(e);
                    handleRegionChange(e.target.value, setFieldValue);
                  }}
                  error={touched.regionId && !!errors.regionId}
                >
                  {regions.map((r) => (
                    <MenuItem key={r.id} value={r.id}>
                      {r.regionName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Branch - Dynamic */}
              <FormControl fullWidth sx={{ gridColumn: "span 2" }}>
                <InputLabel>Branch *</InputLabel>
                <Select
                  name="branchId"
                  value={values.branchId}
                  onChange={handleChange}
                  disabled={!values.regionId || branches.length === 0}
                  error={touched.branchId && !!errors.branchId}
                >
                  {branches.length === 0 ? (
                    <MenuItem disabled>
                      {values.regionId ? "Loading branches..." : "Select region first"}
                    </MenuItem>
                  ) : (
                    branches.map((b) => (
                      <MenuItem key={b.value} value={b.value}>
                        {b.label}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>

              {/* Account Name */}
              <TextField
                fullWidth
                label="Account Name *"
                name="accountName"
                value={values.accountName}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.accountName && !!errors.accountName}
                helperText={touched.accountName && errors.accountName}
                sx={{ gridColumn: "span 2" }}
              />

              {/* Till/Paybill Number */}
              {/* <TextField
                fullWidth
                label="Till / Paybill Number *"
                name="tillPaybillNumber"
                value={values.tillPaybillNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.tillPaybillNumber && !!errors.tillPaybillNumber}
                helperText={touched.tillPaybillNumber && errors.tillPaybillNumber}
                sx={{ gridColumn: "span 2" }}
              /> */}

              {/* Account Type – Sale Account Checkbox */}
            <FormControlLabel
            sx={{ gridColumn: "span 4" }}
            control={
                <Checkbox
                name="isSaleAccount"
                checked={values.isSaleAccount}
                onChange={(e) => setFieldValue("isSaleAccount", e.target.checked)}
                />
            }
            label="Is Sale Account"
            />


              {/* Description */}
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description"
                name="accountDescription"
                value={values.accountDescription}
                onChange={handleChange}
                sx={{ gridColumn: "span 4" }}
              />
            </Box>

            <Box display="flex" justifyContent="end" mt="20px">
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving..." : isEditing ? "Update" : "Create"}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default MpesaAccountForm;